import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../../components/Component";
import { Modal, ModalBody, Form, Nav, NavItem, NavLink, TabContent, TabPane, Spinner } from "reactstrap";
import classnames from "classnames";
import { useForm } from "react-hook-form";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const paginatedRows = (res) => (Array.isArray(res?.data) ? res.data : []);

const QUESTION_TYPES = [
  { value: "boolean", label: "Oui / Non" },
  { value: "text", label: "Texte" },
  { value: "numeric", label: "Numérique" },
  { value: "photo", label: "Photo" },
  { value: "rating", label: "Notation" },
];

const recurrenceLabel = (r) =>
  ({ daily: "Quotidien", weekly: "Hebdomadaire", monthly: "Mensuel" }[r] || "—");

const userLabel = (u) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "—");

const HseProcessInspectionsPage = () => {
  const history = useHistory();
  const basePath = `${process.env.PUBLIC_URL || ""}/hse-process/inspections`;

  const [activeTab, setActiveTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tplModal, setTplModal] = useState(false);
  const [tplEditId, setTplEditId] = useState(null);
  const [tplItems, setTplItems] = useState([]);
  const [insModal, setInsModal] = useState(false);
  const [insEditModal, setInsEditModal] = useState(false);
  const [insEditId, setInsEditId] = useState(null);
  const [insEditLoading, setInsEditLoading] = useState(false);
  const [insEditInspection, setInsEditInspection] = useState(null);
  const [deleteTplModal, setDeleteTplModal] = useState(false);
  const [deleteTplId, setDeleteTplId] = useState(null);
  const [deleteTplName, setDeleteTplName] = useState("");
  const [users, setUsers] = useState([]);
  const [recurrenceRules, setRecurrenceRules] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [ruleModal, setRuleModal] = useState(false);
  const [itemPerPage] = useState(10);
  const [tplSearch, setTplSearch] = useState("");
  const [tplPage, setTplPage] = useState(1);
  const [insSearch, setInsSearch] = useState("");
  const [insPage, setInsPage] = useState(1);
  const [ruleSearch, setRuleSearch] = useState("");
  const [rulePage, setRulePage] = useState(1);
  const [tplTriedSubmit, setTplTriedSubmit] = useState(false);
  const [tplStep, setTplStep] = useState(1);
  const [dragTplIndex, setDragTplIndex] = useState(null);
  const [dragOverTplIndex, setDragOverTplIndex] = useState(null);

  const tplForm = useForm({ shouldUnregister: false });
  const insForm = useForm();
  const insEditForm = useForm({
    defaultValues: { inspection_template_id: "", site_id: "", asset_id: "", assigned_to_user_id: "" },
  });
  const ruleForm = useForm();
  const { register: tplRegister, handleSubmit: tplSubmit, reset: tplReset, watch: tplWatch } = tplForm;
  const { register: insRegister, handleSubmit: insSubmit, reset: insReset } = insForm;
  const { register: insEditRegister, handleSubmit: insEditSubmit, reset: insEditReset, watch: insEditWatch } = insEditForm;
  const { register: ruleRegister, handleSubmit: ruleSubmit, reset: ruleReset, watch: ruleWatch } = ruleForm;

  const templateName = tplWatch("name")?.trim() || "";
  const validTplItemsCount = tplItems.filter((x) => x.label && x.label.trim()).length;
  const canSaveTemplate = !!templateName && validTplItemsCount > 0;

  const moveTplItem = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= tplItems.length) return;
    const next = [...tplItems];
    const [moved] = next.splice(index, 1);
    next.splice(targetIndex, 0, moved);
    setTplItems(next.map((item, idx) => ({ ...item, sort_order: idx })));
  };

  const reorderTplItems = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
    const next = [...tplItems];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setTplItems(next.map((item, idx) => ({ ...item, sort_order: idx })));
  };

  const goToTemplateStep2 = () => {
    if (!templateName) {
      setTplTriedSubmit(true);
      toast.error("Le nom du modèle est obligatoire.");
      return;
    }
    setTplTriedSubmit(false);
    setTplStep(2);
  };

  const loadTemplates = async () => {
    try {
      const res = await api.getInspectionTemplates({ per_page: 100 });
      setTemplates(paginatedRows(res));
    } catch (e) {
      toast.error(e.message || "Erreur chargement modèles");
      setTemplates([]);
    }
  };

  const loadInspections = async () => {
    try {
      const res = await api.getInspections({ per_page: 100 });
      setInspections(paginatedRows(res));
    } catch (e) {
      toast.error(e.message || "Erreur chargement inspections");
      setInspections([]);
    }
  };

  const loadSitesAndAssets = async () => {
    try {
      const [sRes, aRes] = await Promise.all([
        api.getSites(),
        api.getAssets({ per_page: 200 }).catch(() => ({ data: [] })),
      ]);
      const sRows = Array.isArray(sRes?.data) ? sRes.data : Array.isArray(sRes) ? sRes : [];
      setSites(sRows);
      setAssets(paginatedRows(aRes));
    } catch (e) {
      toast.error("Erreur chargement sites / actifs");
    }
  };

  const loadRecurrenceRules = useCallback(async () => {
    setScheduleLoading(true);
    try {
      const res = await api.getInspectionRecurrenceRules({ per_page: 100 });
      setRecurrenceRules(paginatedRows(res));
    } catch (e) {
      toast.error(e.message || "Erreur chargement planification");
      setRecurrenceRules([]);
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadTemplates(), loadInspections(), loadSitesAndAssets()]).finally(() => setLoading(false));
    (async () => {
      try {
        const uRes = await api.getUsers({ per_page: 400 });
        setUsers(paginatedRows(uRes));
      } catch (e) {
        setUsers([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (activeTab === "planification") {
      loadRecurrenceRules();
    }
  }, [activeTab, loadRecurrenceRules]);

  useEffect(() => {
    setTplPage(1);
  }, [tplSearch]);
  useEffect(() => {
    setInsPage(1);
  }, [insSearch]);
  useEffect(() => {
    setRulePage(1);
  }, [ruleSearch]);

  const openNewTemplate = () => {
    setTplEditId(null);
    setTplTriedSubmit(false);
    setTplStep(1);
    setTplItems([
      {
        question_type: "boolean",
        label: "",
        required: true,
        sort_order: 0,
        weight: 1,
        help_text: "",
        compliance_rule: { expected: true },
        collapsed: false,
      },
    ]);
    tplReset({
      name: "",
      description: "",
      is_active: true,
      default_assignee_user_id: "",
      recurrence: "",
      create_incident_on_non_compliance: false,
    });
    setTplModal(true);
  };

  const openEditTemplate = async (id) => {
    try {
      const t = await api.getInspectionTemplate(id);
      setTplEditId(id);
      setTplTriedSubmit(false);
      setTplStep(1);
      tplReset({
        name: t.name || "",
        description: t.description || "",
        is_active: t.is_active !== false,
        default_assignee_user_id: t.default_assignee_user_id ? String(t.default_assignee_user_id) : "",
        recurrence: t.recurrence || "",
        create_incident_on_non_compliance: !!t.create_incident_on_non_compliance,
      });
      const items = (t.items || []).map((it, i) => ({
        question_type: it.question_type,
        label: it.label,
        required: !!it.required,
        sort_order: it.sort_order ?? i,
        weight: it.weight ?? 1,
        help_text: it.help_text || "",
        compliance_rule: it.compliance_rule || {},
        collapsed: false,
      }));
      setTplItems(
        items.length
          ? items
          : [
              {
                question_type: "boolean",
                label: "",
                required: true,
                sort_order: 0,
                weight: 1,
                help_text: "",
                compliance_rule: { expected: true },
                collapsed: false,
              },
            ]
      );
      setTplModal(true);
    } catch (e) {
      toast.error(e.message || "Erreur chargement modèle");
    }
  };

  const onSaveTemplate = async (data) => {
    if (tplStep === 1) {
      goToTemplateStep2();
      return;
    }

    setTplTriedSubmit(true);
    if (!data.name || !data.name.trim()) {
      toast.error("Le nom du modèle est obligatoire.");
      return;
    }

    const payload = {
      name: data.name.trim(),
      description: data.description || null,
      is_active: data.is_active === true || data.is_active === "true" || data.is_active === "on",
      default_assignee_user_id: data.default_assignee_user_id ? parseInt(data.default_assignee_user_id, 10) : null,
      recurrence: data.recurrence || null,
      create_incident_on_non_compliance:
        data.create_incident_on_non_compliance === true ||
        data.create_incident_on_non_compliance === "true" ||
        data.create_incident_on_non_compliance === "on",
      items: tplItems
        .filter((r) => r.label && r.label.trim())
        .map((r, i) => ({
          question_type: r.question_type,
          label: r.label.trim(),
          required: !!r.required,
          sort_order: r.sort_order ?? i,
          weight: parseFloat(r.weight) || 1,
          help_text: r.help_text || null,
          compliance_rule: r.compliance_rule && Object.keys(r.compliance_rule).length ? r.compliance_rule : null,
        })),
    };
    if (!payload.items.length) {
      toast.error("Ajoutez au moins une question avec un libellé.");
      return;
    }
    try {
      if (tplEditId) {
        await api.updateInspectionTemplate(tplEditId, payload);
        toast.success("Modèle mis à jour");
      } else {
        await api.createInspectionTemplate(payload);
        toast.success("Modèle créé");
      }
      setTplModal(false);
      loadTemplates();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur enregistrement");
    }
  };

  const confirmDeleteTemplate = (t) => {
    setDeleteTplId(t.id);
    setDeleteTplName(t.name);
    setDeleteTplModal(true);
  };

  const doDeleteTemplate = async () => {
    if (!deleteTplId) return;
    try {
      await api.deleteInspectionTemplate(deleteTplId);
      toast.success("Modèle supprimé");
      setDeleteTplModal(false);
      loadTemplates();
    } catch (e) {
      toast.error(e.message || "Suppression impossible");
    }
  };

  const openNewInspection = () => {
    insReset({ inspection_template_id: "", site_id: "", asset_id: "", assigned_to_user_id: "" });
    setInsModal(true);
  };

  const closeInsEditModal = () => {
    setInsEditModal(false);
    setInsEditId(null);
    setInsEditInspection(null);
    setInsEditLoading(false);
    insEditReset({ inspection_template_id: "", site_id: "", asset_id: "", assigned_to_user_id: "" });
  };

  const openInspectionFromList = (row) => {
    if (row.status === "submitted") {
      history.push(`${basePath}/${row.id}`);
      return;
    }
    void openEditInspectionModal(row);
  };

  const openEditInspectionModal = async (row) => {
    setInsEditId(row.id);
    setInsEditModal(true);
    setInsEditLoading(true);
    setInsEditInspection(null);
    try {
      const inv = await api.getInspection(row.id);
      setInsEditInspection(inv);
    } catch (e) {
      toast.error(e.message || "Chargement impossible");
      closeInsEditModal();
    } finally {
      setInsEditLoading(false);
    }
  };

  const onSaveEditInspection = async (data) => {
    if (!insEditId) return;
    try {
      const body = {
        inspection_template_id: parseInt(data.inspection_template_id, 10),
        site_id: parseInt(data.site_id, 10),
        asset_id: data.asset_id ? parseInt(data.asset_id, 10) : null,
        assigned_to_user_id: data.assigned_to_user_id ? parseInt(data.assigned_to_user_id, 10) : null,
      };
      await api.updateInspection(insEditId, body);
      toast.success("Inspection mise à jour");
      closeInsEditModal();
      loadInspections();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur enregistrement");
    }
  };

  const onCreateInspection = async (data) => {
    try {
      const tplId = parseInt(data.inspection_template_id, 10);
      const tpl = templates.find((x) => x.id === tplId);
      let assignedTo = data.assigned_to_user_id ? parseInt(data.assigned_to_user_id, 10) : null;
      if (!assignedTo && tpl?.default_assignee_user_id) {
        assignedTo = tpl.default_assignee_user_id;
      }
      const body = {
        inspection_template_id: tplId,
        site_id: parseInt(data.site_id, 10),
        status: "draft",
      };
      if (data.asset_id) body.asset_id = parseInt(data.asset_id, 10);
      if (assignedTo) body.assigned_to_user_id = assignedTo;
      const created = await api.createInspection(body);
      setInsModal(false);
      toast.success("Inspection créée");
      loadInspections();
      history.push(`${basePath}/${created.id}`);
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur création");
    }
  };

  const statusBadge = (s) => {
    const colorMap = {
      draft: "secondary",
      in_progress: "info",
      submitted: "success",
    };
    const labelMap = {
      draft: "Brouillon",
      in_progress: "En cours",
      submitted: "Soumis",
    };
    const c = colorMap[s] || "secondary";
    const label = labelMap[s] ?? (s ? String(s) : "—");
    return <span className={`badge text-bg-${c}`}>{label}</span>;
  };

  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("fr-FR") : "—");

  const openNewRule = () => {
    ruleReset({
      inspection_template_id: "",
      site_id: "",
      asset_id: "",
      recurrence: "weekly",
      assigned_to_user_id: "",
    });
    setRuleModal(true);
  };

  const onSaveRule = async (data) => {
    const body = {
      inspection_template_id: parseInt(data.inspection_template_id, 10),
      site_id: parseInt(data.site_id, 10),
      recurrence: data.recurrence,
      assigned_to_user_id: data.assigned_to_user_id ? parseInt(data.assigned_to_user_id, 10) : null,
      asset_id: data.asset_id ? parseInt(data.asset_id, 10) : null,
    };
    try {
      await api.createInspectionRecurrenceRule(body);
      toast.success("Règle créée — prochaine exécution selon le planning serveur");
      setRuleModal(false);
      loadRecurrenceRules();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur");
    }
  };

  const ruleSiteId = ruleWatch("site_id");
  const assetsForRuleSite = ruleSiteId
    ? assets.filter((a) => String(a.site_id) === String(ruleSiteId))
    : assets;

  const insEditSiteId = insEditWatch("site_id");
  const assetsForInsEditModal = insEditSiteId
    ? assets.filter((a) => String(a.site_id) === String(insEditSiteId))
    : assets;

  useEffect(() => {
    if (!insEditModal || !insEditInspection) return;
    const inv = insEditInspection;
    const uid = inv.assigned_to_user_id ?? inv.assignedTo?.id ?? null;
    const tid = inv.inspection_template_id ?? inv.template?.id ?? "";
    const sid = inv.site_id ?? inv.site?.id ?? "";
    const aid = inv.asset_id ?? inv.asset?.id ?? null;
    insEditReset({
      inspection_template_id: tid !== "" && tid != null ? String(tid) : "",
      site_id: sid !== "" && sid != null ? String(sid) : "",
      asset_id: aid != null ? String(aid) : "",
      assigned_to_user_id: uid != null ? String(uid) : "",
    });
  }, [insEditModal, insEditInspection, insEditReset]);

  const norm = (v) => String(v ?? "").toLowerCase();
  const filteredTemplates = templates.filter((t) => {
    if (!tplSearch.trim()) return true;
    const q = norm(tplSearch);
    return (
      norm(t.name).includes(q) ||
      norm(recurrenceLabel(t.recurrence)).includes(q) ||
      norm(userLabel(t.default_assignee)).includes(q) ||
      String(t.items_count ?? "").includes(q.trim())
    );
  });
  const tplLast = tplPage * itemPerPage;
  const tplFirst = tplLast - itemPerPage;
  const pageTemplates = filteredTemplates.slice(tplFirst, tplLast);

  const filteredInspections = inspections.filter((row) => {
    if (!insSearch.trim()) return true;
    const q = norm(insSearch);
    return (
      String(row.id).includes(q.trim()) ||
      norm(row.template?.name).includes(q) ||
      norm(row.site?.name).includes(q) ||
      norm(userLabel(row.assigned_to || row.assignedTo)).includes(q) ||
      norm(row.status).includes(q)
    );
  });
  const insLast = insPage * itemPerPage;
  const insFirst = insLast - itemPerPage;
  const pageInspections = filteredInspections.slice(insFirst, insLast);

  const filteredRules = recurrenceRules.filter((r) => {
    if (!ruleSearch.trim()) return true;
    const q = norm(ruleSearch);
    return (
      norm(r.template?.name).includes(q) ||
      norm(r.site?.name).includes(q) ||
      norm(r.asset?.name).includes(q) ||
      norm(recurrenceLabel(r.recurrence)).includes(q)
    );
  });
  const ruleLast = rulePage * itemPerPage;
  const ruleFirst = ruleLast - itemPerPage;
  const pageRules = filteredRules.slice(ruleFirst, ruleLast);

  return (
    <React.Fragment>
      <Head title="Inspections HSE" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Inspections (checklists)</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Nav tabs className="mt-n3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "templates" })}
                onClick={() => setActiveTab("templates")}
                href="#"
              >
                Modèles
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "inspections" })}
                onClick={() => setActiveTab("inspections")}
                href="#"
              >
                Inspections
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "planification" })}
                onClick={() => setActiveTab("planification")}
                href="#"
              >
                Planification
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="templates">
              <BlockBetween className="mb-3">
                <BlockHeadContent>
                  <div className="form-control-wrap" style={{ minWidth: 260 }}>
                    <div className="form-icon form-icon-left">
                      <Icon name="search" />
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher un modèle…"
                      value={tplSearch}
                      onChange={(e) => setTplSearch(e.target.value)}
                    />
                  </div>
                  {!loading && (
                    <BlockDes className="text-soft mt-1 mb-0">
                      <p className="small mb-0">
                        {tplSearch
                          ? `${filteredTemplates.length} modèle(s) sur ${templates.length}`
                          : `${templates.length} modèle(s)`}
                      </p>
                    </BlockDes>
                  )}
                </BlockHeadContent>
                <BlockHeadContent>
                  <ul className="nk-block-tools g-3">
                    <PermissionGate permission={["Inspections - Créer", "Inspections - Modifier"]}>
                      <li>
                        <Button color="primary" size="md" onClick={openNewTemplate}>
                          <Icon name="plus" />
                          <span>Nouveau modèle</span>
                        </Button>
                      </li>
                    </PermissionGate>
                  </ul>
                </BlockHeadContent>
              </BlockBetween>
              {loading ? (
                <div className="card card-stretch">
                  <div className="card-inner text-center py-5">
                    <Spinner />
                  </div>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="card card-stretch">
                  <div className="card-inner text-center py-5">
                    <p className="text-soft mb-0">
                      {tplSearch ? "Aucun modèle ne correspond à la recherche" : "Aucun modèle — créez-en un pour commencer"}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <DataTable className="card-stretch">
                    <DataTableBody>
                      <DataTableHead className="nk-tb-item nk-tb-head">
                        <DataTableRow>
                          <span className="sub-text">Nom</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Questions</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Actif</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Fréquence</span>
                        </DataTableRow>
                        <DataTableRow size="lg">
                          <span className="sub-text">Assigné défaut</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Incident si NOK</span>
                        </DataTableRow>
                        <DataTableRow className="nk-tb-col-tools text-end">
                          <span className="sub-text">Actions</span>
                        </DataTableRow>
                      </DataTableHead>
                      {pageTemplates.map((t) => (
                        <DataTableItem key={t.id}>
                          <DataTableRow>
                            <span className="tb-lead">{t.name}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span className="tb-sub">{t.items_count ?? t.items?.length ?? "-"}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span className={`badge text-bg-${t.is_active ? "success" : "gray"}`}>{t.is_active ? "Oui" : "Non"}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span className="tb-sub">{recurrenceLabel(t.recurrence)}</span>
                          </DataTableRow>
                          <DataTableRow size="lg">
                            <span className="tb-sub">{userLabel(t.default_assignee)}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span className={`badge text-bg-${t.create_incident_on_non_compliance ? "warning" : "gray"}`}>
                              {t.create_incident_on_non_compliance ? "Oui" : "Non"}
                            </span>
                          </DataTableRow>
                          <DataTableRow className="nk-tb-col-tools">
                            <ul className="nk-tb-actions gx-1">
                              <PermissionGate permission={["Inspections - Modifier"]}>
                                <li>
                                  <button
                                    type="button"
                                    className="btn btn-trigger btn-icon"
                                    title="Modifier"
                                    onClick={() => openEditTemplate(t.id)}
                                  >
                                    <Icon name="edit" />
                                  </button>
                                </li>
                              </PermissionGate>
                              <PermissionGate permission={["Inspections - Supprimer"]}>
                                <li>
                                  <button
                                    type="button"
                                    className="btn btn-trigger btn-icon text-danger"
                                    title="Supprimer"
                                    onClick={() => confirmDeleteTemplate(t)}
                                  >
                                    <Icon name="trash" />
                                  </button>
                                </li>
                              </PermissionGate>
                            </ul>
                          </DataTableRow>
                        </DataTableItem>
                      ))}
                    </DataTableBody>
                  </DataTable>
                  <div className="card-inner">
                    <PaginationComponent
                      itemPerPage={itemPerPage}
                      totalItems={filteredTemplates.length}
                      paginate={setTplPage}
                      currentPage={tplPage}
                    />
                  </div>
                </>
              )}
            </TabPane>
            <TabPane tabId="inspections">
              <BlockBetween className="mb-3">
                <BlockHeadContent>
                  <div className="form-control-wrap" style={{ minWidth: 260 }}>
                    <div className="form-icon form-icon-left">
                      <Icon name="search" />
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher (id, site, modèle…)…"
                      value={insSearch}
                      onChange={(e) => setInsSearch(e.target.value)}
                    />
                  </div>
                  {!loading && (
                    <BlockDes className="text-soft mt-1 mb-0">
                      <p className="small mb-0">
                        {insSearch
                          ? `${filteredInspections.length} inspection(s) sur ${inspections.length}`
                          : `${inspections.length} inspection(s)`}
                      </p>
                    </BlockDes>
                  )}
                </BlockHeadContent>
                <BlockHeadContent>
                  <ul className="nk-block-tools g-3">
                    <PermissionGate permission={["Inspections - Créer"]}>
                      <li>
                        <Button color="primary" size="md" onClick={openNewInspection}>
                          <Icon name="plus" />
                          <span>Nouvelle inspection</span>
                        </Button>
                      </li>
                    </PermissionGate>
                  </ul>
                </BlockHeadContent>
              </BlockBetween>
              {loading ? (
                <div className="card card-stretch">
                  <div className="card-inner text-center py-5">
                    <Spinner />
                  </div>
                </div>
              ) : filteredInspections.length === 0 ? (
                <div className="card card-stretch">
                  <div className="card-inner text-center py-5">
                    <p className="text-soft mb-0">
                      {insSearch ? "Aucune inspection ne correspond à la recherche" : "Aucune inspection"}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <DataTable className="card-stretch">
                    <DataTableBody>
                      <DataTableHead className="nk-tb-item nk-tb-head">
                        <DataTableRow size="md">
                          <span className="sub-text">ID</span>
                        </DataTableRow>
                        <DataTableRow size="xl">
                          <span className="sub-text">Modèle</span>
                        </DataTableRow>
                        <DataTableRow size="lg">
                          <span className="sub-text">Site</span>
                        </DataTableRow>
                        <DataTableRow size="lg">
                          <span className="sub-text">Assigné</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Statut</span>
                        </DataTableRow>
                        <DataTableRow className="nk-tb-col-tools text-end">
                          <span className="sub-text">Actions</span>
                        </DataTableRow>
                      </DataTableHead>
                      {pageInspections.map((row) => (
                        <DataTableItem key={row.id}>
                          <DataTableRow size="md">
                            <span className="tb-lead">#{row.id}</span>
                          </DataTableRow>
                          <DataTableRow size="xl">
                            <span className="tb-sub">{row.template?.name || row.inspection_template_id}</span>
                          </DataTableRow>
                          <DataTableRow size="lg">
                            <span className="tb-sub">{row.site?.name || row.site_id}</span>
                          </DataTableRow>
                          <DataTableRow size="lg">
                            <span className="tb-sub">{userLabel(row.assigned_to || row.assignedTo)}</span>
                          </DataTableRow>
                          <DataTableRow size="md">{statusBadge(row.status)}</DataTableRow>
                          <DataTableRow className="nk-tb-col-tools">
                            <ul className="nk-tb-actions gx-1">
                              <li>
                                <button
                                  type="button"
                                  className="btn btn-trigger btn-icon"
                                  title={row.status === "submitted" ? "Voir la fiche" : "Modifier / ouvrir la checklist"}
                                  onClick={() => openInspectionFromList(row)}
                                >
                                  <Icon name={row.status === "submitted" ? "eye" : "edit"} />
                                </button>
                              </li>
                            </ul>
                          </DataTableRow>
                        </DataTableItem>
                      ))}
                    </DataTableBody>
                  </DataTable>
                  <div className="card-inner">
                    <PaginationComponent
                      itemPerPage={itemPerPage}
                      totalItems={filteredInspections.length}
                      paginate={setInsPage}
                      currentPage={insPage}
                    />
                  </div>
                </>
              )}
            </TabPane>
            <TabPane tabId="planification">
              <p className="text-soft small mb-3">
                Chaque règle génère une inspection en <strong>brouillon</strong> à l’échéance (commande planifiée côté
                serveur). L’utilisateur qui crée la règle est enregistré comme « démarreur » ; l’assigné terrain peut être
                précisé ici ou repris du modèle.
              </p>
              <BlockBetween className="mb-3">
                <BlockHeadContent>
                  <div className="form-control-wrap" style={{ minWidth: 260 }}>
                    <div className="form-icon form-icon-left">
                      <Icon name="search" />
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Rechercher une règle…"
                      value={ruleSearch}
                      onChange={(e) => setRuleSearch(e.target.value)}
                    />
                  </div>
                  {!scheduleLoading && (
                    <BlockDes className="text-soft mt-1 mb-0">
                      <p className="small mb-0">
                        {ruleSearch
                          ? `${filteredRules.length} règle(s) sur ${recurrenceRules.length}`
                          : `${recurrenceRules.length} règle(s)`}
                      </p>
                    </BlockDes>
                  )}
                </BlockHeadContent>
                <BlockHeadContent>
                  <ul className="nk-block-tools g-3">
                    <PermissionGate permission={["Inspections - Créer"]}>
                      <li>
                        <Button color="primary" size="md" onClick={openNewRule}>
                          <Icon name="plus" />
                          <span>Nouvelle règle</span>
                        </Button>
                      </li>
                    </PermissionGate>
                  </ul>
                </BlockHeadContent>
              </BlockBetween>
              {scheduleLoading ? (
                <div className="card card-stretch">
                  <div className="card-inner text-center py-5">
                    <Spinner />
                  </div>
                </div>
              ) : filteredRules.length === 0 ? (
                <div className="card card-stretch">
                  <div className="card-inner text-center py-5">
                    <p className="text-soft mb-0">
                      {ruleSearch ? "Aucune règle ne correspond à la recherche" : "Aucune règle de planification"}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <DataTable className="card-stretch">
                    <DataTableBody>
                      <DataTableHead className="nk-tb-item nk-tb-head">
                        <DataTableRow size="xl">
                          <span className="sub-text">Modèle</span>
                        </DataTableRow>
                        <DataTableRow size="lg">
                          <span className="sub-text">Site</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Actif</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Récurrence</span>
                        </DataTableRow>
                        <DataTableRow size="lg">
                          <span className="sub-text">Prochaine</span>
                        </DataTableRow>
                        <DataTableRow size="lg">
                          <span className="sub-text">Dernière gén.</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="sub-text">Active</span>
                        </DataTableRow>
                        <DataTableRow className="nk-tb-col-tools text-end">
                          <span className="sub-text">Actions</span>
                        </DataTableRow>
                      </DataTableHead>
                      {pageRules.map((r) => (
                        <DataTableItem key={r.id}>
                          <DataTableRow size="xl">
                            <span className="tb-lead">{r.template?.name || r.inspection_template_id}</span>
                          </DataTableRow>
                          <DataTableRow size="lg">
                            <span className="tb-sub">{r.site?.name || r.site_id}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span className="tb-sub">{r.asset?.name || "—"}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span className="tb-sub">{recurrenceLabel(r.recurrence)}</span>
                          </DataTableRow>
                          <DataTableRow size="lg">
                            <span className="tb-sub small">{fmtDateTime(r.next_run_at)}</span>
                          </DataTableRow>
                          <DataTableRow size="lg">
                            <span className="tb-sub small">{fmtDateTime(r.last_generated_at)}</span>
                          </DataTableRow>
                          <DataTableRow size="md">
                            <span className={`badge text-bg-${r.is_active ? "success" : "gray"}`}>{r.is_active ? "Oui" : "Non"}</span>
                          </DataTableRow>
                          <DataTableRow className="nk-tb-col-tools">
                            <ul className="nk-tb-actions gx-1">
                              <PermissionGate permission={["Inspections - Modifier"]}>
                                <li>
                                  <button
                                    type="button"
                                    className="btn btn-trigger btn-icon"
                                    title={r.is_active ? "Mettre en pause" : "Réactiver"}
                                    onClick={async () => {
                                      try {
                                        await api.updateInspectionRecurrenceRule(r.id, { is_active: !r.is_active });
                                        toast.success(r.is_active ? "Règle mise en pause" : "Règle réactivée");
                                        loadRecurrenceRules();
                                      } catch (e) {
                                        toast.error(e.message || "Erreur");
                                      }
                                    }}
                                  >
                                    <Icon name={r.is_active ? "pause" : "play"} />
                                  </button>
                                </li>
                              </PermissionGate>
                              <PermissionGate permission={["Inspections - Supprimer"]}>
                                <li>
                                  <button
                                    type="button"
                                    className="btn btn-trigger btn-icon text-danger"
                                    title="Supprimer"
                                    onClick={async () => {
                                      if (!window.confirm("Supprimer cette règle ?")) return;
                                      try {
                                        await api.deleteInspectionRecurrenceRule(r.id);
                                        toast.success("Règle supprimée");
                                        loadRecurrenceRules();
                                      } catch (e) {
                                        toast.error(e.message || "Erreur");
                                      }
                                    }}
                                  >
                                    <Icon name="trash" />
                                  </button>
                                </li>
                              </PermissionGate>
                            </ul>
                          </DataTableRow>
                        </DataTableItem>
                      ))}
                    </DataTableBody>
                  </DataTable>
                  <div className="card-inner">
                    <PaginationComponent
                      itemPerPage={itemPerPage}
                      totalItems={filteredRules.length}
                      paginate={setRulePage}
                      currentPage={rulePage}
                    />
                  </div>
                </>
              )}
            </TabPane>
          </TabContent>
        </Block>

        <Modal
          isOpen={tplModal}
          toggle={() => {
            setTplModal(false);
            setTplStep(1);
          }}
          className="modal-lg"
        >
          <div className="modal-header">
            <h5 className="modal-title">{tplEditId ? "Modifier le modèle" : "Nouveau modèle"}</h5>
            <button type="button" className="close" onClick={() => setTplModal(false)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={tplSubmit(onSaveTemplate)}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="overline-title mb-0">Etape {tplStep}/2</h6>
                <div className="text-soft small">
                  <span className={tplStep === 1 ? "text-primary fw-bold" : ""}>1) Informations générales</span>
                  <span className="mx-2">-</span>
                  <span className={tplStep === 2 ? "text-primary fw-bold" : ""}>2) Checklist / Questions</span>
                </div>
              </div>

              <div className={tplStep === 1 ? "" : "d-none"}>
                  <h6 className="overline-title">1) Informations générales</h6>
                  <div className="border rounded p-3 mb-3">
                <div className="form-group">
                  <label className="form-label">Nom du modèle *</label>
                  <input className="form-control" name="name" ref={tplRegister({ required: true })} />
                  {tplTriedSubmit && !templateName ? <p className="text-danger small mt-1 mb-0">Le nom est obligatoire.</p> : null}
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" name="description" rows={2} ref={tplRegister} />
                </div>
                <div className="form-group">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="tpl_active"
                      name="is_active"
                      defaultChecked
                      ref={tplRegister}
                    />
                    <label className="custom-control-label" htmlFor="tpl_active">
                      Modèle actif
                    </label>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Assigné terrain par défaut</label>
                    <select className="form-select" name="default_assignee_user_id" ref={tplRegister}>
                      <option value="">— Aucun —</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {userLabel(u)} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Fréquence indicative</label>
                    <select className="form-select" name="recurrence" ref={tplRegister}>
                      <option value="">— Non planifiée —</option>
                      <option value="daily">Quotidien</option>
                      <option value="weekly">Hebdomadaire</option>
                      <option value="monthly">Mensuel</option>
                    </select>
                  </div>
                </div>
                <div className="form-group mt-4">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="tpl_incident_nok"
                      name="create_incident_on_non_compliance"
                      ref={tplRegister}
                    />
                    <label className="custom-control-label" htmlFor="tpl_incident_nok">
                      Créer aussi un incident pour chaque réponse non conforme (en plus de l’observation)
                    </label>
                  </div>
                </div>
                  </div>
              </div>

              <div className={tplStep === 2 ? "" : "d-none"}>
                  <h6 className="overline-title mt-3">2) Checklist / Questions</h6>
                  <p className="text-soft small mb-2">Ajoutez au moins une question avec un libellé.</p>
              {tplItems.map((row, idx) => (
                <div
                  key={idx}
                  className={`border rounded mb-2 ${dragOverTplIndex === idx ? "border-primary" : ""}`}
                  draggable
                  onDragStart={() => {
                    setDragTplIndex(idx);
                    setDragOverTplIndex(idx);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragOverTplIndex !== idx) setDragOverTplIndex(idx);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    reorderTplItems(dragTplIndex, idx);
                    setDragTplIndex(null);
                    setDragOverTplIndex(null);
                  }}
                  onDragEnd={() => {
                    setDragTplIndex(null);
                    setDragOverTplIndex(null);
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center px-3 pt-3 pb-2">
                    <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                      <Button
                        type="button"
                        color="light"
                        size="sm"
                        className="btn-icon p-0 flex-shrink-0"
                        style={{ width: "1.65rem", height: "1.65rem", minWidth: "1.65rem" }}
                        title={row.collapsed ? "Développer" : "Réduire"}
                        onClick={(e) => {
                          e.stopPropagation();
                          const next = [...tplItems];
                          next[idx] = { ...next[idx], collapsed: !next[idx].collapsed };
                          setTplItems(next);
                        }}
                      >
                        <Icon
                          name={row.collapsed ? "chevron-down" : "chevron-up"}
                          style={{ fontSize: "0.8rem", lineHeight: 1 }}
                        />
                      </Button>
                      <h6 className="title mb-0 text-nowrap flex-shrink-0">Question {idx + 1}</h6>
                      {row.collapsed ? (
                        <span className="text-soft small text-truncate" title={(row.label || "").trim() || "Sans libellé"}>
                          {(row.label || "").trim() || "Sans libellé"}
                        </span>
                      ) : null}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <span className="badge text-bg-light">{row.question_type || "boolean"}</span>
                      <span className="text-soft small" title="Glissez pour réordonner">
                        <Icon name="menu" />
                      </span>
                    </div>
                  </div>
                  {!row.collapsed ? (
                  <div className="px-3 pb-3">
                  <div className="row">
                    <div className="col-md-4">
                      <label className="form-label">Type</label>
                      <select
                        className="form-select"
                        value={row.question_type}
                        onChange={(e) => {
                          const next = [...tplItems];
                          next[idx] = { ...next[idx], question_type: e.target.value };
                          setTplItems(next);
                        }}
                      >
                        {QUESTION_TYPES.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Libellé *</label>
                      <input
                        className="form-control"
                        value={row.label}
                        onChange={(e) => {
                          const next = [...tplItems];
                          next[idx] = { ...next[idx], label: e.target.value };
                          setTplItems(next);
                        }}
                      />
                      {tplStep === 2 && tplTriedSubmit && !(row.label || "").trim() ? (
                        <p className="text-danger small mt-1 mb-0">Le libellé de la question est obligatoire.</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-md-4">
                      <label className="form-label">Pondération</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={row.weight}
                        onChange={(e) => {
                          const next = [...tplItems];
                          next[idx] = { ...next[idx], weight: e.target.value };
                          setTplItems(next);
                        }}
                      />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`req_${idx}`}
                          checked={!!row.required}
                          onChange={(e) => {
                            const next = [...tplItems];
                            next[idx] = { ...next[idx], required: e.target.checked };
                            setTplItems(next);
                          }}
                        />
                        <label className="custom-control-label" htmlFor={`req_${idx}`}>
                          Obligatoire
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="form-group mt-2">
                    <label className="form-label">Aide / consigne (optionnel)</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={row.help_text || ""}
                      onChange={(e) => {
                        const next = [...tplItems];
                        next[idx] = { ...next[idx], help_text: e.target.value };
                        setTplItems(next);
                      }}
                    />
                  </div>
                  {row.question_type === "boolean" && (
                    <div className="form-group mt-2">
                      <label className="form-label">Conforme si (JSON optionnel, ex. {`{"expected":true}`})</label>
                      <input
                        className="form-control font-monospace small"
                        placeholder='{"expected":true}'
                        value={typeof row.compliance_rule === "object" ? JSON.stringify(row.compliance_rule) : "{}"}
                        onChange={(e) => {
                          const next = [...tplItems];
                          try {
                            next[idx] = { ...next[idx], compliance_rule: JSON.parse(e.target.value || "{}") };
                          } catch {
                            next[idx] = { ...next[idx], compliance_rule: {} };
                          }
                          setTplItems(next);
                        }}
                      />
                    </div>
                  )}
                  <div className="d-flex gap-2 mt-2">
                    <Button color="light" size="sm" type="button" onClick={() => moveTplItem(idx, -1)} disabled={idx === 0}>
                      Haut
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => moveTplItem(idx, 1)}
                      disabled={idx === tplItems.length - 1}
                    >
                      Bas
                    </Button>
                    <Button
                      color="link"
                      className="text-danger p-0"
                      type="button"
                      onClick={() => setTplItems(tplItems.filter((_, i) => i !== idx).map((item, i) => ({ ...item, sort_order: i })))}
                    >
                      Retirer cette question
                    </Button>
                  </div>
                  </div>
                  ) : null}
                </div>
              ))}
              <Button
                color="light"
                size="sm"
                className="mb-3"
                type="button"
                onClick={() =>
                  setTplItems([
                    ...tplItems,
                    {
                      question_type: "boolean",
                      label: "",
                      required: true,
                      sort_order: tplItems.length,
                      weight: 1,
                      help_text: "",
                      compliance_rule: { expected: true },
                      collapsed: false,
                    },
                  ])
                }
              >
                + Question
              </Button>
              {tplStep === 2 && tplTriedSubmit && !canSaveTemplate ? (
                <p className="text-danger small mb-2">Complétez le nom du modèle et au moins une question valide avant d’enregistrer.</p>
              ) : null}
              </div>
              <div className="text-end">
                {tplStep === 2 ? (
                  <>
                    <Button color="light" className="me-2" type="button" onClick={() => setTplStep(1)}>
                      Precedent
                    </Button>
                    <Button color="primary" type="submit" disabled={!canSaveTemplate}>
                      Enregistrer
                    </Button>
                  </>
                ) : (
                  <Button color="primary" type="button" onClick={goToTemplateStep2}>
                    Suivant
                  </Button>
                )}
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal isOpen={ruleModal} toggle={() => setRuleModal(false)}>
          <div className="modal-header">
            <h5 className="modal-title">Nouvelle règle de planification</h5>
            <button type="button" className="close" onClick={() => setRuleModal(false)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={ruleSubmit(onSaveRule)}>
              <div className="form-group">
                <label className="form-label">Modèle *</label>
                <select className="form-select" name="inspection_template_id" ref={ruleRegister({ required: true })}>
                  <option value="">—</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Site *</label>
                <select className="form-select" name="site_id" ref={ruleRegister({ required: true })}>
                  <option value="">—</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Actif (optionnel)</label>
                <select className="form-select" name="asset_id" ref={ruleRegister}>
                  <option value="">—</option>
                  {assetsForRuleSite.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Récurrence *</label>
                <select className="form-select" name="recurrence" ref={ruleRegister({ required: true })}>
                  <option value="daily">Quotidien</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="monthly">Mensuel</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Assigné terrain (optionnel)</label>
                <select className="form-select" name="assigned_to_user_id" ref={ruleRegister}>
                  <option value="">— Défaut du modèle —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {userLabel(u)} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-end">
                <Button color="primary" type="submit">
                  Créer la règle
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal isOpen={insModal} toggle={() => setInsModal(false)}>
          <div className="modal-header">
            <h5 className="modal-title">Nouvelle inspection</h5>
            <button type="button" className="close" onClick={() => setInsModal(false)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={insSubmit(onCreateInspection)}>
              <div className="form-group">
                <label className="form-label">Modèle *</label>
                <select className="form-select" name="inspection_template_id" ref={insRegister({ required: true })}>
                  <option value="">—</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Site *</label>
                <select className="form-select" name="site_id" ref={insRegister({ required: true })}>
                  <option value="">—</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Actif (optionnel)</label>
                <select className="form-select" name="asset_id" ref={insRegister}>
                  <option value="">—</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Assigné terrain (optionnel)</label>
                <select className="form-select" name="assigned_to_user_id" ref={insRegister}>
                  <option value="">— Utiliser l’assigné par défaut du modèle —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {userLabel(u)} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-end">
                <Button color="primary" type="submit">
                  Créer
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal isOpen={insEditModal} toggle={closeInsEditModal} className="modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">{insEditId ? `Modifier l'inspection #${insEditId}` : "Inspection"}</h5>
            <button type="button" className="close" onClick={closeInsEditModal}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            {insEditLoading ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : insEditInspection ? (
              <Form
                key={insEditId || "ins-edit"}
                className="is-alter"
                onSubmit={insEditSubmit(onSaveEditInspection)}
              >
                <p className="text-soft small mb-3">
                  En <strong>brouillon</strong> ou <strong>en cours</strong>, vous pouvez modifier le{" "}
                  <strong>modèle</strong>, le <strong>site</strong>, l&apos;<strong>actif</strong> et l&apos;
                  <strong>assigné terrain</strong>. Si vous <strong>changez de modèle</strong>, les réponses déjà
                  saisies et le score associé sont <strong>supprimés côté serveur</strong> (nouvelle checklist). Les
                  réponses se complètent sur la fiche détail via <strong>Ouvrir la checklist</strong>.
                </p>
                <div className="form-group">
                  <label className="form-label">Modèle *</label>
                  <select
                    className="form-select"
                    name="inspection_template_id"
                    ref={insEditRegister({ required: true })}
                  >
                    <option value="">—</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Site *</label>
                  <select className="form-select" name="site_id" ref={insEditRegister({ required: true })}>
                    <option value="">—</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Actif (optionnel)</label>
                  <select className="form-select" name="asset_id" ref={insEditRegister}>
                    <option value="">—</option>
                    {assetsForInsEditModal.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Statut</label>
                  <div>{statusBadge(insEditInspection.status)}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Assigné terrain</label>
                  <select className="form-select" name="assigned_to_user_id" ref={insEditRegister}>
                    <option value="">— Aucun —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {userLabel(u)} ({u.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-end">
                  <Button
                    color="light"
                    className="me-2"
                    type="button"
                    onClick={() => {
                      history.push(`${basePath}/${insEditId}`);
                      closeInsEditModal();
                    }}
                  >
                    Ouvrir la checklist
                  </Button>
                  <Button color="primary" type="submit">
                    Enregistrer
                  </Button>
                </div>
              </Form>
            ) : (
              <p className="text-soft mb-0">Impossible d&apos;afficher cette inspection.</p>
            )}
          </ModalBody>
        </Modal>

        <Modal isOpen={deleteTplModal} toggle={() => setDeleteTplModal(false)}>
          <ModalBody>
            <p>
              Supprimer le modèle <strong>{deleteTplName}</strong> ?
            </p>
            <div className="text-end">
              <Button color="light" className="me-2" onClick={() => setDeleteTplModal(false)}>
                Annuler
              </Button>
              <Button color="danger" onClick={doDeleteTemplate}>
                Supprimer
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default HseProcessInspectionsPage;
